import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MessageSquare,
  Bookmark,
  Sparkles,
  ArrowRight,
  GraduationCap,
  Clock,
  CheckCircle2,
  Share2,
  Tag as TagIcon,
} from 'lucide-react'
import { Avatar } from '../shared/Avatar'
import { TimeAgo } from '../shared/TimeAgo'
import { VerifiedBadge } from '../shared/VerifiedBadge'
import { colors, radii, typography } from '../../styles/tokens'
import type { QuestionSummary } from '../../api/qa'
import { qaApi } from '../../api/qa'
import toast from 'react-hot-toast'

interface QuestionCardProps {
  question: QuestionSummary
  onToggleSave?: (questionId: string, isSaved: boolean) => void
}

export function QuestionCard({ question, onToggleSave }: QuestionCardProps) {
  const navigate = useNavigate()
  const [isSaved, setIsSaved] = useState(!!question.is_saved)
  const [saving, setSaving] = useState(false)

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (saving) return
    setSaving(true)
    const prev = isSaved
    setIsSaved(!prev)
    try {
      const res = await qaApi.toggleSave('question', question.id)
      const nextSaved = res.data.is_saved
      setIsSaved(nextSaved)
      onToggleSave?.(question.id, nextSaved)
      toast.success(nextSaved ? 'Question saved to bookmarks' : 'Question removed from saved', {
        id: `save-${question.id}`,
      })
    } catch {
      setIsSaved(prev)
      toast.error('Failed to update bookmark')
    } finally {
      setSaving(false)
    }
  }

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const url = `${window.location.origin}/app/alumni/questions/${question.id}`
    if (navigator.share) {
      try {
        await navigator.share({
          title: question.title,
          text: `Check out this student question on Campnect: "${question.title}"`,
          url,
        })
        return
      } catch {}
    }
    await navigator.clipboard.writeText(url)
    toast.success('Link copied to clipboard!', { id: 'copy-share' })
  }

  const preview = question.most_helpful_answer_preview

  return (
    <div
      onClick={() => navigate(`/app/alumni/questions/${question.id}`)}
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 20,
        padding: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        cursor: 'pointer',
        transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--color-border-hover, rgba(17,17,20,0.22))'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--color-border)'
      }}
    >
      {/* Top row: Category pill + Status + Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: '11.5px',
              fontWeight: 700,
              fontFamily: typography.fontDisplay,
              padding: '3px 10px',
              borderRadius: radii.pill,
              background: 'var(--color-bg)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border)',
            }}
          >
            {question.category}
          </span>

          {question.status === 'answered' || question.answers_count > 0 ? (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                color: '#15803D',
                background: 'rgba(34,197,94,0.1)',
                padding: '2px 8px',
                borderRadius: radii.pill,
              }}
            >
              <CheckCircle2 size={12} />
              Answered ({question.answers_count})
            </span>
          ) : (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                padding: '2px 8px',
                borderRadius: radii.pill,
              }}
            >
              Needs Answer
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            onClick={handleShare}
            aria-label="Share question"
            title="Share"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Share2 size={15} />
          </button>

          <button
            onClick={handleSave}
            aria-label="Save question"
            title={isSaved ? 'Remove from saved' : 'Save question'}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: isSaved ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Bookmark size={16} fill={isSaved ? 'currentColor' : 'none'} />
          </button>
        </div>
      </div>

      {/* Student Author details */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Avatar
          src={question.student_avatar}
          name={question.student_name}
          size={36}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                fontSize: '13.5px',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
              }}
            >
              {question.student_name}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
              @{question.student_username}
            </span>
          </div>

          <div
            style={{
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '1px',
            }}
          >
            {question.student_college && (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {question.student_college}
              </span>
            )}
            {question.student_course && <span>· {question.student_course}</span>}
            <span>·</span>
            <TimeAgo date={question.created_at} />
          </div>
        </div>
      </div>

      {/* Question Title & Description */}
      <div>
        <h3
          style={{
            margin: '0 0 6px',
            fontSize: '16.5px',
            fontWeight: 700,
            lineHeight: 1.35,
            color: 'var(--color-text-primary)',
            fontFamily: typography.fontDisplay,
          }}
        >
          {question.title}
        </h3>
        <p
          style={{
            margin: 0,
            fontSize: '13.5px',
            lineHeight: 1.55,
            color: 'var(--color-text-secondary)',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {question.description}
        </p>
      </div>

      {/* Tags */}
      {question.tags && question.tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {question.tags.map((tag, i) => (
            <span
              key={i}
              style={{
                fontSize: '11px',
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                padding: '2px 8px',
                borderRadius: '6px',
              }}
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Tagged Alumni preview */}
      {question.tagged_alumni && question.tagged_alumni.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            borderRadius: radii.md,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
          }}
        >
          <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--color-text-muted)', flexShrink: 0 }}>
            Directed to:
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflowX: 'auto', minWidth: 0 }}>
            {question.tagged_alumni.map((alm) => (
              <span
                key={alm.id}
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap',
                }}
              >
                <Avatar src={alm.avatar_url} name={alm.name} size={18} />
                {alm.name}
                {alm.is_verified === 1 && <VerifiedBadge size={11} />}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Most Helpful Answer Preview */}
      {preview && (
        <div
          style={{
            marginTop: '2px',
            padding: '12px 14px',
            borderRadius: radii.md,
            background: 'var(--color-bg)',
            borderLeft: '3px solid #111114',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Sparkles size={13} style={{ color: 'var(--color-text-primary)' }} />
              <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                Helpful Answer by {preview.author_name}
              </span>
              {preview.is_verified === 1 && <VerifiedBadge size={12} />}
              <span style={{ fontSize: '11.5px', color: 'var(--color-text-muted)' }}>
                ({preview.author_title} · {preview.author_company})
              </span>
            </div>
            {preview.helpful_count > 0 && (
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#15803D' }}>
                +{preview.helpful_count} helpful
              </span>
            )}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: '12.5px',
              lineHeight: 1.5,
              color: 'var(--color-text-secondary)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            "{preview.content}"
          </p>
        </div>
      )}

      {/* Bottom row: Discussion link */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '8px',
          borderTop: '1px solid var(--color-border)',
          marginTop: '2px',
        }}
      >
        <span style={{ fontSize: '12.5px', color: 'var(--color-text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          <MessageSquare size={14} />
          <strong>{question.answers_count}</strong> {question.answers_count === 1 ? 'answer' : 'answers'}
        </span>

        <span
          style={{
            fontSize: '12.5px',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            fontFamily: typography.fontDisplay,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          View discussion <ArrowRight size={13} />
        </span>
      </div>
    </div>
  )
}
